import { useState } from "react";

import { Clock, Download, Lock, LockOpen, Upload } from "lucide-react";
import { useDispatch } from "react-redux";

import { Banner, BannerIcon, BannerTitle } from "@components/ui/banner";
import { Button } from "@components/ui/button";

import {
  useMarkVinciUploaded,
  useUnlockReport,
} from "@hooks/mutations/use-report-finalisation";
import { useDownloadResponsePdf } from "@hooks/mutations/use-templates";
import { useFinalisationState } from "@hooks/queries/use-finalisation-state";

import { setMessage } from "@redux/notification";

import { getFriendlyErrorMessage } from "@utils/notification";

/**
 * Admin-only finalisation controls (MDRS-271 §6). Shown on the report tab for
 * admins: download the frozen PDF (chain of custody for the manual Vinci
 * upload), mark it uploaded to Vinci, and break-glass unlock with a mandatory
 * reason. Hidden entirely for non-admins — the doctor never sees these.
 */
export const AdminFinalisationControls = ({
  responseId,
  caseNoticeShown = false,
}: {
  responseId: string;
  /** The case page's banner already says the report can't be finalised yet
   * (documents still arriving, or their status unknown — MDRS-508). The
   * "awaiting doctor finalisation" notice would only repeat it, so it waits
   * until that banner clears. */
  caseNoticeShown?: boolean;
}) => {
  const dispatch = useDispatch();
  const { data: state, isLoading } = useFinalisationState(responseId);
  const unlock = useUnlockReport();
  const markUploaded = useMarkVinciUploaded();
  const downloadPdf = useDownloadResponsePdf();

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [reason, setReason] = useState("");

  const onError = (err: unknown) =>
    dispatch(setMessage({ type: "error", content: getFriendlyErrorMessage(err) }));

  const handleDownload = async () => {
    try {
      await downloadPdf.mutateAsync({ responseId, fileName: "report.pdf" });
    } catch (err) {
      onError(err);
    }
  };

  const handleMarkUploaded = async () => {
    try {
      await markUploaded.mutateAsync({ responseId });
      dispatch(
        setMessage({ type: "success", content: "Marked as uploaded to Vinci." })
      );
    } catch (err) {
      onError(err);
    }
  };

  const handleUnlock = async () => {
    try {
      await unlock.mutateAsync({ responseId, reason });
      dispatch(
        setMessage({
          type: "success",
          content: "Report unlocked. The case is editable again.",
        })
      );
      setUnlockOpen(false);
      setReason("");
    } catch (err) {
      onError(err);
    }
  };

  if (isLoading || !state) return null;

  const finalised = !!state.finalised_at;
  if (!finalised) {
    if (caseNoticeShown) return null;
    return (
      <Banner variant="brand" inset>
        <BannerIcon>
          <Clock />
        </BannerIcon>
        <BannerTitle>
          Awaiting doctor finalisation. Once the doctor finalises, the report can be
          downloaded and marked as uploaded to Vinci here.
        </BannerTitle>
      </Banner>
    );
  }

  const vinci = state.vinci_upload_status ?? "pending";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-background-white px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm">
          <Lock className="size-4 text-warning" />
          <span className="font-medium">Finalised &amp; locked</span>
          <span className="text-foreground-muted">
            {state.finalised_at
              ? `on ${new Date(state.finalised_at).toLocaleString()}`
              : null}
          </span>
          <span className="ml-2 rounded-full border border-border px-2 py-0.5 text-xs capitalize">
            Vinci: {vinci.replace(/_/g, " ")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* secondary-subtle (blue tinted) — a genuine secondary action, not
              a plain dismiss, so it keeps the branded treatment. */}
          <Button
            size="sm"
            variant="secondary-subtle"
            onClick={handleDownload}
            isLoading={downloadPdf.isPending}
          >
            <Download className="mr-1.5 size-4" />
            Download PDF
          </Button>
          {vinci !== "uploaded" && (
            <Button
              size="sm"
              variant="simple-outline"
              onClick={handleMarkUploaded}
              isLoading={markUploaded.isPending}
            >
              <Upload className="mr-1.5 size-4" />
              Mark uploaded to Vinci
            </Button>
          )}
          <Button
            size="sm"
            variant="simple-outline"
            onClick={() => setUnlockOpen((o) => !o)}
          >
            <LockOpen className="mr-1.5 size-4" />
            Unlock
          </Button>
        </div>
      </div>

      {unlockOpen && (
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
          <label htmlFor="unlock-reason" className="text-sm font-medium">
            Reason for unlocking (required — this is audit-logged)
          </label>
          <textarea
            id="unlock-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="e.g. Doctor requested a correction to the medical opinion."
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setUnlockOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleUnlock}
              disabled={!reason.trim()}
              isLoading={unlock.isPending}
            >
              Confirm unlock
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
